import { FC, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ArrowRightIcon, IconColor, XmarkIcon } from "../../icons";
import { ImageSrc, useWindowDimension } from "../../utils";
import cx from "classnames";

import styles from "./ModalPhotoCarousel.module.scss";

export interface ModalPhotoCarouselProps {
  photos: string[];
  photoIndex: number;
  setPhotoIndex: (i: number) => void;
  onClose: () => void;
}

const incrementIndex = (index: number, arrayLength: number) => {
  if (index < arrayLength - 1) return index + 1;
  return 0;
};

const decrementIndex = (index: number, arrayLength: number) => {
  if (index > 0) return index - 1;
  return arrayLength - 1;
};

export const ModalPhotoCarousel: FC<ModalPhotoCarouselProps> = ({
  photos,
  photoIndex,
  setPhotoIndex,
  onClose,
}) => {
  const photosCount = photos.length;
  const [widthWindow] = useWindowDimension();
  const imgRef = useRef<HTMLImageElement>(null);
  const [widthArrow, setWifthArrow] = useState(
    imgRef.current?.getBoundingClientRect().left
  );
  const sizeXMark = widthWindow <= 600 ? 24 : 32;
  const sizeArrow = widthWindow <= 600 ? 32 : 50;

  const onKeypress = (e: any) => {
    if (e.key === "ArrowRight") {
      setPhotoIndex(incrementIndex(photoIndex, photosCount));
    }
    if (e.key === "ArrowLeft") {
      setPhotoIndex(decrementIndex(photoIndex, photosCount));
    }
  };

  useEffect(() => {
    setWifthArrow(imgRef.current?.getBoundingClientRect().left);
  }, [widthWindow, photoIndex]);

  useEffect(() => {
    window.addEventListener("keydown", onKeypress);

    return () => {
      window.removeEventListener("keydown", onKeypress);
    };
  }, [photoIndex]);

  return (
    <div>
      <>
        {createPortal(
          <div className={styles.popupCarousel} onClick={onClose}>
            <div
              className={styles.modalPhotoContainer}
              onClick={(e) => e.stopPropagation()}
            >
              <div
                className={cx(styles.arrowBlock, styles.arrowBlockLeft)}
                style={{
                  width: widthArrow
                    ? widthArrow < 50
                      ? "150px"
                      : widthArrow
                    : "150px",
                  opacity: widthArrow ? (widthArrow < 50 ? 0 : 1) : 0,
                }}
                onClick={() => {
                  setPhotoIndex(decrementIndex(photoIndex, photosCount));
                }}
              >
                <ArrowRightIcon
                  width={sizeArrow}
                  height={sizeArrow}
                  className={cx(styles.arrow, styles.arrowLeft, {
                    disabled: photosCount < 2,
                  })}
                />
              </div>
              <div className={styles.modalPhoto}>
                <div className={styles.modalPhotoHeader}>
                  <div className={styles.modalPhotoHeaderText}>
                    {photoIndex + 1}/{photosCount}
                  </div>
                  <XmarkIcon
                    width={sizeXMark}
                    height={sizeXMark}
                    onClick={onClose}
                    fill={IconColor.WHITE}
                    className={styles.modalPhotoXmark}
                  />
                </div>
                <img
                  ref={imgRef}
                  src={photos[photoIndex]}
                  className={styles.photo}
                  draggable={false}
                />
              </div>
              <div
                className={cx(styles.arrowBlock, styles.arrowBlockRight)}
                style={{
                  width: widthArrow
                    ? widthArrow < 50
                      ? "150px"
                      : widthArrow
                    : "150px",
                  opacity: widthArrow ? (widthArrow < 50 ? 0 : 1) : 0,
                }}
                onClick={() => {
                  setPhotoIndex(incrementIndex(photoIndex, photosCount));
                }}
              >
                <ArrowRightIcon
                  width={sizeArrow}
                  height={sizeArrow}
                  className={cx(styles.arrow, {
                    disabled: photosCount < 2,
                  })}
                />
              </div>
            </div>
          </div>,
          document.body
        )}
      </>
    </div>
  );
};
